import {
  FeatureGateEnvironment,
  PerimeterType,
} from '@atlaskit/feature-gate-js-client';
import { EnvType, getEnv } from './env';

interface Config {
  analytics: {
    scheme: string;
    server: string;
  };
  identity: {
    baseUrl: string;
  };
  statsigApiKey: string;
  featureGateEnvironment: FeatureGateEnvironment;
  featureGatePerimeter: PerimeterType;
  /** Shared consent URLs (privacy, terms) */
  consent: {
    /** Atlassian Privacy Policy. */
    privacyPolicyUrl: string;
    /** Atlassian Customer Agreement (Terms of Use). */
    termsOfUseUrl: string;
  };
  /** Shared support URLs */
  support: {
    /** Atlassian customer support landing page. */
    contactSupportUrl: string;
  };
  /** Trello web URLs */
  trello: {
    /** Host used to build workspace web addresses, e.g. `trello.com/w/<slug>`. */
    webHost: string;
  };
  /** Navigation logo destinations */
  navigation: {
    homeUrl?: string;
  };
  /** A2A consent URLs */
  a2a: {
    /** Documentation page for A2A / Rovo agents. */
    docUrl: string;
  };
  /** TWG consent URLs */
  twg: {
    /** Admin/help guide for managing TWG CLI permissions. */
    permissionsManagementDocUrl: string;
  };
  /** MCP consent: policy API and UI toggles */
  mcpPolicy: {
    /** Base URL for GET /policy/check; empty string uses `window.location.origin` */
    apiBaseUrl: string;
    showSseDeprecationBanner: boolean;
    /** Help link for MCP redirect allowlist (redirect-not-allowed banner). */
    allowlistGuideUrl: string;
    /** Documentation page explaining the Rovo MCP Server. */
    rovoDocUrl: string;
    /** Developer docs — OAuth 2.0 3LO apps (Learn more / migration guide). */
    developerDocUrl: string;
    /** SSE migration guide URL. */
    sseMigrationGuideUrl: string;
    /** Admin guide for managing MCP permission policies. */
    permissionsManagementDocUrl: string;
    /** The deprecated SSE endpoint shown in the SSE deprecation banner. */
    deprecatedSseEndpoint: string;
  };
}

// default configs is also config for production env
const defaultConfig: Config = {
  analytics: {
    scheme: 'https',
    server: 'analytics.atlassian.com',
  },
  identity: {
    baseUrl: 'https://id.atlassian.com',
  },
  statsigApiKey: '503c2782-4db6-45dc-a57b-0f9b4e3a8636',
  featureGateEnvironment: FeatureGateEnvironment.Production,
  featureGatePerimeter: PerimeterType.COMMERCIAL,
  consent: {
    privacyPolicyUrl:
      'https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers',
    termsOfUseUrl:
      'https://www.atlassian.com/legal/atlassian-customer-agreement#intro',
  },
  support: {
    contactSupportUrl: 'http://support.atlassian.com/',
  },
  trello: {
    webHost: 'trello.com',
  },
  navigation: {
    homeUrl: 'https://home.atlassian.com',
  },
  a2a: {
    docUrl:
      'https://support.atlassian.com/security-and-access-policies/docs/understand-atlassian-rovo-mcp-server/',
  },
  twg: {
    permissionsManagementDocUrl:
      'https://developer.atlassian.com/cloud/twg-cli/',
  },
  mcpPolicy: {
    apiBaseUrl: '',
    showSseDeprecationBanner: false,
    allowlistGuideUrl:
      'https://support.atlassian.com/security-and-access-policies/docs/control-atlassian-rovo-mcp-server-settings/',
    rovoDocUrl:
      'https://support.atlassian.com/security-and-access-policies/docs/understand-atlassian-rovo-mcp-server/',
    developerDocUrl:
      'https://support.atlassian.com/atlassian-rovo-mcp-server/docs/getting-started-with-the-atlassian-remote-mcp-server/',
    sseMigrationGuideUrl:
      'https://support.atlassian.com/security-and-access-policies/docs/control-atlassian-rovo-mcp-server-settings/',
    permissionsManagementDocUrl:
      'https://support.atlassian.com/security-and-access-policies/docs/control-atlassian-rovo-mcp-server-settings/',
    deprecatedSseEndpoint: 'https://mcp.atlassian.com/v1/sse',
  },
};

const stagingOverrides: Partial<Config> = {
  statsigApiKey: '9f0ef96e-59da-4618-9844-397dc60d1b0d',
  featureGateEnvironment: FeatureGateEnvironment.Staging,
  identity: {
    baseUrl: 'https://id.stg.internal.atlassian.com',
  },
  trello: {
    webHost: 'trellis.coffee',
  },
};

const localAndTestOverrides = {
  statsigApiKey: '1565260a-fa4c-4df1-a23d-87427576e44e',
  featureGateEnvironment: FeatureGateEnvironment.Development,
  identity: {
    baseUrl: 'https://id-domain.dev.internal.atlassian.com',
  },
};

const environmentOverrides: Record<
  Exclude<EnvType, 'prod'>,
  Partial<Config>
> = {
  local: {
    ...localAndTestOverrides,
    analytics: {
      scheme: 'http',
      server: `${window.location.hostname}:3000`,
    },
  },
  ddev: {
    ...stagingOverrides,
    ...localAndTestOverrides,
  },
  stg: stagingOverrides,
  'stg-fedm': {
    ...stagingOverrides,
    featureGatePerimeter: PerimeterType.FEDRAMP_MODERATE,
    navigation: {
      // No approved government Home destination; do not inherit the default.
      homeUrl: undefined,
    },
    identity: {
      baseUrl: 'https://id.stg.atlassian-us-gov-mod.com',
    },
  },
  'prod-fedm': {
    featureGatePerimeter: PerimeterType.FEDRAMP_MODERATE,
    navigation: {
      // No approved government Home destination; do not inherit the default.
      homeUrl: undefined,
    },
    identity: {
      baseUrl: 'https://id.atlassian-us-gov-mod.com',
    },
  },
};

const cachedConfig: any = {};
export function getConfig(): Config {
  const env = getEnv();

  if (!cachedConfig[env]) {
    cachedConfig[env] = {
      ...defaultConfig,
      ...environmentOverrides[
        env as keyof Record<Exclude<EnvType, 'prod'>, Partial<Config>>
      ],
    };
  }
  return cachedConfig[env];
}
